<script setup lang="ts">
// Who may see and operate the environment (its access list) and the installation it belongs to
// (docs/proposal-environment-access.md). The owner edits the members, an admin also the owner; an operator sets
// the installation id. The API decides; this only offers what the caller's role allows.
import { computed, ref, watch } from 'vue';
import { must } from '../api/client';
import type { Environment } from '../api/types';
import type { components } from '../api/schema';
import { useContext } from '../context';
import ProblemAlert from './ProblemAlert.vue';
import Icon from './Icon.vue';
import { roleTone } from '../status/status';

type Member = components['schemas']['Member'];

const props = defineProps<{ env: Environment }>();
const emit = defineEmits<{ changed: [] }>();
const { api } = useContext();

const USERNAME = /^[A-Za-z0-9][A-Za-z0-9._@-]{0,127}$/;
const INSTALLATION = /^[A-Z][A-Z0-9]*-[0-9]+$/;

const role = computed(() => props.env.yourRole);
const admin = computed(() => role.value === 'admin');
const canEditMembers = computed(() => admin.value || (role.value === 'owner' && props.env.dataAccess));
const canSetInstallation = computed(() => admin.value || role.value === 'owner' || role.value === 'operator');

const editing = ref(false);
const members = ref<Member[]>([]);
const owner = ref('');
const newUser = ref('');
const newRole = ref<'viewer' | 'operator'>('viewer');
const installation = ref('');
const error = ref<unknown>();
const message = ref<string>();
const formError = ref<string>();

function reset() {
  members.value = (props.env.access.members ?? []).map((m) => ({ ...m }));
  owner.value = props.env.access.owner?.username ?? '';
  installation.value = props.env.installationId ?? '';
  formError.value = undefined;
}
watch(() => props.env, () => { if (!editing.value) reset(); }, { immediate: true });

function add() {
  const u = newUser.value.trim();
  formError.value = undefined;
  if (!USERNAME.test(u)) {
    formError.value = `"${u}" is not a username`;
    return;
  }
  if (u.toLowerCase() === owner.value.toLowerCase() || members.value.some((m) => m.username.toLowerCase() === u.toLowerCase())) {
    formError.value = `${u} is already on the list`;
    return;
  }
  members.value.push({ username: u, role: newRole.value });
  newUser.value = '';
}

async function save() {
  error.value = undefined;
  message.value = undefined;
  const ownerChanged = admin.value && owner.value.trim() && owner.value.trim() !== (props.env.access.owner?.username ?? '');
  if (ownerChanged && !USERNAME.test(owner.value.trim())) {
    formError.value = `"${owner.value}" is not a username`;
    return;
  }
  try {
    must(await api.PUT('/environments/{name}/access', {
      params: { path: { name: props.env.name } },
      body: {
        ...(ownerChanged ? { owner: { username: owner.value.trim() } } : {}),
        members: members.value.map((m) => ({ ...(m.subject ? { subject: m.subject } : {}), username: m.username, role: m.role })),
      },
    }));
    editing.value = false;
    message.value = 'Access list saved.';
    emit('changed');
  } catch (e) {
    error.value = e;
  }
}

async function saveInstallation() {
  error.value = undefined;
  message.value = undefined;
  const v = installation.value.trim().toUpperCase();
  if (v && !INSTALLATION.test(v)) {
    formError.value = `"${v}" is not an installation id like SKISUP-22329`;
    return;
  }
  formError.value = undefined;
  try {
    must(await api.PUT('/environments/{name}/installation-id', { params: { path: { name: props.env.name } }, body: { installationId: v || null } }));
    message.value = v ? `Installation id set to ${v}.` : 'Installation id removed.';
    emit('changed');
  } catch (e) {
    error.value = e;
  }
}
</script>

<template>
  <section class="card" aria-labelledby="access-h" data-testid="access">
    <div class="card-head">
      <h2 id="access-h">People with access</h2>
      <span class="muted" style="font-size: 13px">Changing this list restarts and rebuilds nothing.</span>
    </div>

    <div v-if="env.dataClassification === 'production-raw'" class="alert banner" style="margin-bottom: 0.75rem">
      <Icon name="lock" :size="18" />
      <div class="grow"><strong>Raw production data</strong> of <code>{{ env.source.customer }}/{{ env.source.environment }}</code>: members see the environment's status, but its logs and actions also need the entitlement to it in the backup service.</div>
    </div>

    <div class="person">
      <span class="avatar lg" aria-hidden="true">{{ (env.access.owner?.username ?? '?').slice(0, 2).toUpperCase() }}</span>
      <div class="who"><div style="font-weight: 600" data-testid="access-owner">{{ env.access.owner?.username ?? 'none: an admin assigns one' }}</div><div class="sub">owns the environment</div></div>
      <span class="pill sm tone-dark">owner</span>
    </div>
    <template v-if="!editing">
      <div v-for="m in env.access.members" :key="m.username" class="person">
        <span class="avatar lg grey" aria-hidden="true">{{ m.username.slice(0, 2).toUpperCase() }}</span>
        <div class="who"><div style="font-weight: 600">{{ m.username }}</div></div>
        <span class="pill sm" :class="roleTone(m.role)">{{ m.role }}</span>
      </div>
      <p v-if="!env.access.members.length" class="muted" style="margin: 0.5rem 0">Not shared with anyone yet.</p>
    </template>

    <p class="muted" style="margin: 0.75rem 0 0; font-size: 13px">
      You: <span data-testid="access-you"><span class="pill sm" :class="roleTone(role)">{{ role ?? '—' }}</span><span v-if="!env.dataAccess"> · not entitled to this raw data</span></span>
      · Data: {{ env.dataClassification }} ({{ env.dataClassification === 'production-raw' ? 'members also need the entitlement to ' + env.source.customer + '/' + env.source.environment : 'the list is enough' }})
    </p>

    <button v-if="canEditMembers && !editing" type="button" class="primary" style="margin-top: 1rem" data-testid="access-edit" @click="reset(); editing = true"><Icon name="share" /> Edit access…</button>

    <form v-if="editing" novalidate style="display: flex; flex-direction: column; gap: 0" @submit.prevent="save">
      <div v-for="(m, i) in members" :key="m.username" class="person">
        <span class="avatar lg grey" aria-hidden="true">{{ m.username.slice(0, 2).toUpperCase() }}</span>
        <div class="who"><div style="font-weight: 600">{{ m.username }}</div><div v-if="!env.access.members.some((x) => x.username === m.username)" class="sub" style="color: var(--accent)">added · not saved yet</div></div>
        <select v-model="m.role" :aria-label="`Role of ${m.username}`" style="width: auto; min-height: 32px">
          <option value="viewer">viewer</option>
          <option value="operator">operator</option>
        </select>
        <button type="button" class="sm" :aria-label="`Remove ${m.username}`" @click="members.splice(i, 1)">Remove</button>
      </div>
      <p v-if="!members.length" class="muted">No members.</p>
      <div class="inline" style="align-items: flex-end; padding-top: 1rem; border-top: 1px solid var(--line-soft); gap: 8px">
        <div class="field" style="flex: 1 1 240px">
          <label for="access-new-user">Add a person</label>
          <input id="access-new-user" v-model="newUser" placeholder="username, on stage the e-mail address" data-testid="access-new-user" @keydown.enter.prevent="add" />
        </div>
        <div class="field">
          <label for="access-new-role">Role</label>
          <select id="access-new-role" v-model="newRole" style="width: auto">
            <option value="viewer">viewer</option>
            <option value="operator">operator</option>
          </select>
        </div>
        <button type="button" data-testid="access-add" @click="add">Add</button>
      </div>
      <p class="hint" style="margin-top: 8px">A viewer sees the environment and its logs; an operator also stops, starts, changes builds and refreshes data. People are added by username; there is no directory lookup.</p>
      <div v-if="admin" class="field" style="margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--line-soft)">
        <label for="access-owner">Owner <span class="pill sm tone-idle">admins only</span></label>
        <input id="access-owner" v-model="owner" data-testid="access-owner-input" />
        <p class="hint">There is always exactly one owner; only an admin assigns another one.</p>
      </div>
      <div class="actions-row" style="margin-top: 1rem">
        <button type="submit" class="primary" data-testid="access-save">Save access list</button>
        <button type="button" @click="editing = false; reset()">Cancel</button>
      </div>
    </form>

    <p v-if="formError" class="field-error" role="alert" style="margin-top: 0.5rem">{{ formError }}</p>
    <p v-if="message" class="info" role="status" style="margin-top: 0.75rem">{{ message }}</p>
    <div v-if="error" style="margin-top: 0.75rem"><ProblemAlert :error="error" context="The API refused the change" /></div>
  </section>

  <section class="card" aria-labelledby="inst-h" style="margin-top: 1.25rem">
    <h2 id="inst-h">Installation</h2>
    <p class="muted" style="margin: 0 0 0.75rem; font-size: 13px">The Metis installation this environment belongs to, e.g. <code>SKISUP-22329</code>. For finding it only; several environments may share one.</p>
    <form v-if="canSetInstallation" class="inline" novalidate @submit.prevent="saveInstallation">
      <input v-model="installation" class="mono" aria-label="Installation id" placeholder="SKISUP-22329" style="max-width: 14rem" data-testid="installation-input" />
      <button type="submit" data-testid="installation-save">Save</button>
    </form>
    <p v-else class="mono">{{ env.installationId ?? '—' }}</p>
  </section>
</template>
